import { getUiEventTarget } from '../shadowDom';
import { FOUNDATIONS } from '../designFoundations';
import { PALETTE } from '../designPalette';
import { computeTooltipPosition } from './tooltipPosition';
// Same surface design as extension _tooltip.css, including its 400ms delay.
// Resolved token fallbacks also support standalone client review without BEM CSS.
export const toolbarTooltipCss = `
.oq-capture-tooltip{position:fixed;z-index:2147483647;padding:var(--oq-space-1,${FOUNDATIONS.space[1]}) var(--oq-space-2,${FOUNDATIONS.space[2]});background:var(--oq-gray-900,${PALETTE.neutral[900]});color:var(--oq-white,${PALETTE.surface.card});font-family:${FOUNDATIONS.fontFamily.system};font-size:var(--oq-font-size-compact,${FOUNDATIONS.fontSize.compact});font-weight:var(--oq-font-weight-medium,${FOUNDATIONS.fontWeight.medium});line-height:var(--oq-line-height-label,${FOUNDATIONS.lineHeight.label});border-radius:var(--oq-radius-sm,${FOUNDATIONS.radius.sm});box-shadow:0 2px 4px rgba(0,0,0,.15);pointer-events:none;opacity:0;transition:opacity var(--oq-motion-feedback, .15s) var(--oq-ease-out,cubic-bezier(.16,1,.3,1));white-space:nowrap}
.oq-capture-tooltip[data-visible]{opacity:1}
.oq-capture-tooltip[data-wrap]{white-space:normal;max-width:260px;text-wrap:pretty}
.oq-capture-tooltip__arrow{position:absolute;width:0;height:0;border:8px solid transparent;left:calc(50% + 4px);transform:translateX(-50%)}
.oq-capture-tooltip[data-placement="top"] .oq-capture-tooltip__arrow{bottom:-16px;border-top-color:var(--oq-gray-900,${PALETTE.neutral[900]})}
.oq-capture-tooltip[data-placement="bottom"] .oq-capture-tooltip__arrow{top:-16px;border-bottom-color:var(--oq-gray-900,${PALETTE.neutral[900]})}
`;
/** Shared toolbar tooltip behavior for extension, React review and guest review. */
export function mountToolbarTooltips(toolbar, selector = '[data-tooltip]', variantClass = '') {
    const doc = toolbar.ownerDocument;
    const win = doc.defaultView;
    const root = toolbar.getRootNode();
    const portal = root instanceof win.ShadowRoot
        ? root.querySelector('#overlayqa-overlay-container') || root
        : doc.body;
    const bubble = doc.createElement('div');
    bubble.className = ['oq-capture-tooltip', variantClass].filter(Boolean).join(' ');
    bubble.id = `oq-capture-tooltip-${Math.random().toString(36).slice(2, 9)}`;
    bubble.setAttribute('role', 'tooltip');
    const label = doc.createElement('span');
    const arrow = doc.createElement('span');
    arrow.className = 'oq-capture-tooltip__arrow';
    bubble.append(label, arrow);
    let trigger = null;
    let timer;
    const tooltipLabel = (node) => node.dataset.tooltip || node.getAttribute('aria-label') || '';
    function hide() {
        win.clearTimeout(timer);
        timer = undefined;
        trigger?.removeAttribute('aria-describedby');
        trigger = null;
        bubble.removeAttribute('data-visible');
        bubble.remove();
    }
    function position() {
        if (trigger && (!trigger.isConnected || trigger.matches(':disabled'))) {
            hide();
            return;
        }
        if (!trigger || !bubble.isConnected)
            return;
        label.textContent = tooltipLabel(trigger);
        // A sentence-length tooltip (a compare row's related-findings cause) opts
        // into wrapping; every existing one-line label stays nowrap.
        bubble.toggleAttribute('data-wrap', trigger.hasAttribute('data-tooltip-wrap'));
        const rect = trigger.getBoundingClientRect();
        const size = bubble.getBoundingClientRect();
        const coords = computeTooltipPosition({ trigger: rect, tooltip: size, placement: 'top', viewport: { width: win.innerWidth, height: win.innerHeight } });
        bubble.dataset.placement = coords.placement;
        bubble.style.top = `${coords.top}px`;
        bubble.style.left = `${coords.left}px`;
    }
    function show(event) {
        const node = event.target;
        const next = node.closest(selector);
        if (!next || !toolbar.contains(next) || next.matches(':disabled'))
            return;
        // The branded tooltip replaces the browser's title bubble on composer tools.
        if (tooltipLabel(next))
            next.removeAttribute('title');
        if (trigger === next)
            return;
        hide();
        trigger = next;
        timer = win.setTimeout(() => {
            timer = undefined;
            if (!trigger?.isConnected || !trigger.getClientRects().length) {
                hide();
                return;
            }
            label.textContent = tooltipLabel(trigger);
            portal.appendChild(bubble);
            position();
            trigger.setAttribute('aria-describedby', bubble.id);
            bubble.setAttribute('data-visible', '');
        }, 400);
    }
    function leave(event) {
        if (trigger && event.relatedTarget instanceof win.Node && trigger.contains(event.relatedTarget))
            return;
        hide();
    }
    const key = (event) => { if (event.key === 'Escape')
        hide(); };
    const changes = new win.MutationObserver(() => {
        if (trigger && (!trigger.isConnected || trigger.matches(':disabled')))
            hide();
        else if (trigger && label.textContent !== tooltipLabel(trigger))
            position();
    });
    changes.observe(toolbar, { attributes: true, attributeFilter: ['data-tooltip', 'aria-label', 'disabled'], childList: true, subtree: true });
    toolbar.addEventListener('mouseover', show);
    toolbar.addEventListener('mouseout', leave);
    toolbar.addEventListener('focusin', show);
    toolbar.addEventListener('focusout', leave);
    toolbar.addEventListener('pointerdown', hide);
    toolbar.addEventListener('click', hide);
    win.addEventListener('keydown', key, true);
    win.addEventListener('scroll', hide, true);
    win.addEventListener('resize', hide);
    getUiEventTarget().addEventListener('oq:capture-toolbar-moved', hide);
    return { hide, destroy() {
            hide();
            changes.disconnect();
            toolbar.removeEventListener('mouseover', show);
            toolbar.removeEventListener('mouseout', leave);
            toolbar.removeEventListener('focusin', show);
            toolbar.removeEventListener('focusout', leave);
            toolbar.removeEventListener('pointerdown', hide);
            toolbar.removeEventListener('click', hide);
            win.removeEventListener('keydown', key, true);
            win.removeEventListener('scroll', hide, true);
            win.removeEventListener('resize', hide);
            getUiEventTarget().removeEventListener('oq:capture-toolbar-moved', hide);
        } };
}
/** The same pointer/keyboard tooltip on React and vanilla popover composers,
 *  and (with its own selector) on any list whose chips carry `data-tooltip`. */
export function mountComposerTooltips(host, selector = '.oq-composer__tool, .rb-composer__tool') {
    const style = host.ownerDocument.createElement('style');
    style.textContent = toolbarTooltipCss;
    host.appendChild(style);
    const tooltips = mountToolbarTooltips(host, selector);
    return { destroy() { tooltips.destroy(); style.remove(); } };
}
//# sourceMappingURL=toolbarTooltipDom.js.map